import { Component, ElementRef, HostListener, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Subscription } from 'rxjs';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { TimezoneService } from 'src/app/reusables/services/timezone.service';
import { DashboardManager } from 'src/shared/models/dashboard-manager';
import { Dashboard, SaveDashboardModalData, WorkspaceNewTabViewMode, WorkspaceTemplateViewMode } from 'src/shared/models/dashboard.model';
import { ModuleManagerExportFormat } from 'src/shared/models/module-manager/module-manager-options.model';
import { DashboardService } from '../../dashboard.service';
import { ShareViaUrlComponent } from '../../modals/share-via-url/share-via-url.component';
import { ShareViaUrlService } from '../../modals/share-via-url/share-via-url.service';
import { ModuleContainerService } from '../../module-container/module-container.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
 
@Component({
    selector: 'cv-workspace-landing',
    templateUrl: './workspace-landing.component.html',
    styleUrls: ['./workspace-landing.component.scss']
})
export class WorkspaceLandingComponent implements OnInit, OnDestroy {

    @ViewChildren('tabNameInput', { read: ElementRef }) tabNameInputs: QueryList<ElementRef<HTMLInputElement>>;
    @ViewChildren('tabRef', { read: ElementRef }) tabRefs: QueryList<ElementRef<HTMLDivElement>>;

    menuOpen = false;
    isEditingName = false;
    customName: string;
    editName: string;
    assignToolOpened = false;
    dashboardsToAssign: Dashboard[];

    schedulerOpened = false;
    schedulerId: number;
    schedulerKey: string;
    subscriptionTitle: string;
    subscriptionId = -1;

    openSlideout = false;
    filterDashboardManager: DashboardManager = null;

    private closingDashboard = false;
    private _subscriptions: Subscription[] = [];

    constructor(
        private localizationService: CVLocalizationService,
        private shareViaURL: ShareViaUrlService,
        public dashboardService: DashboardService,
        public moduleContainerService: ModuleContainerService,
        public timezoneService: TimezoneService,
    ) { }

    @HostListener('window:beforeunload', ['$event'])
    handleBeforeUnload(e: BeforeUnloadEvent) {
        const needConfirmationToClose = this.dashboardService.dashboards.some(d => d?.NeedConfirmationToClose);

        if (needConfirmationToClose) {
            e.preventDefault();
            e.returnValue = '';
            return;
        }

        delete e.returnValue;
    }

    async ngOnInit() {
        const promises = [
            this.localizationService.GetSection("Common"),
            this.localizationService.GetSection("Dashboard_Tab")
        ];

        this._subscriptions.push(this.dashboardService.discardTemporaryDashboardEvent.subscribe(
            dashboardKey => this.discardTemporaryDashboard(dashboardKey)
        ));

        await Promise.all(promises);
    }

    ngOnDestroy() {
        this._subscriptions.forEach(s => s.unsubscribe());
    }

    getLocalizedText(key: string, defaultValue: string = key) {
        return this.localizationService.getCachedText("Left_Nav_Popup", key, defaultValue);
    }

    /** Verify if a given dashboard is currently active */
    isActiveDashboard(dashboard: Dashboard): boolean {
        if (dashboard)
            return this.dashboardService.activeDashboard.key === dashboard.DashboardKey;
        else
            return this.dashboardService.activeDashboard.key === null;
    }

    get activeDashboardKey(): string {
        return this.dashboardService.activeDashboard.key;
    }

    /** Varify if a given dashboard is a new tab */
    isNewTab(dashboard: Dashboard): boolean {
        return dashboard == null;
    }

    availableDashboardLoaded(dashboard: Dashboard): boolean {
        const dash = this.dashboardService.availableDashboards.find(d => d.DashboardKey === dashboard.DashboardKey);
        return dash !== undefined;
    }

    /** Verify that no new tabs are open */
    noOpenTabs(): boolean {
        return this.dashboardService.dashboards == null || this.dashboardService.dashboards.filter(Boolean).length === 0;    
    }

    /** Show add button if no new tabs are open or less than maximum amount of tabs are open*/
    showAddButton(): boolean {
        return (this.dashboardService.dashboards.length < this.dashboardService.maxAttachedDashboards) &&
            (this.dashboardService.dashboards.indexOf(null) === -1);
    }

    /** Determine if the taskbar should be shown */
    showTaskbar(): boolean {
        if (!this.dashboardService.activeDashboard.key)
            return false;

        const activeDashboard = this.dashboardService.dashboards.filter(Boolean).find(d => d.DashboardKey === this.dashboardService.activeDashboard.key);
        // don't show while new tab or no dashboard
        if (!activeDashboard)
            return false;
        // don't show while dashboard loading
        if (!activeDashboard.states || activeDashboard.states.dashboardModules.loading)
            return false;
        // don't show if dashboard is shared
        if (activeDashboard.RelatedDashboardID > 0)
            return false;

        return true;
    }

    /** When a tab is clicked update selected dashboard and load dashboard */
    selectDashboard(dashboard: Dashboard): void {
        if (dashboard) {
            if (this.dashboardService.activeDashboard.key === dashboard.DashboardKey) {
                this.dashboardService.setActiveDashboard(this.dashboardService.workspaceKey, dashboard.DashboardKey, true);
                return; // If the dashboard is already active, do nothing more (we save the position of the tab)
            }
            this.dashboardService.triggerActiveDashboardChanged(dashboard.DashboardKey, this.dashboardService.activeDashboard.key);
            this.dashboardService.activeDashboard.key = dashboard.DashboardKey;
            this.dashboardService.setActiveDashboard(this.dashboardService.workspaceKey, dashboard.DashboardKey, true);
        } else {
            this.dashboardService.setActiveDashboard(this.dashboardService.workspaceKey, null);
        }
    }

    drop(event: CdkDragDrop<Dashboard[]>) {
        if (!this.tabRefs) return; // If the drop event is not from a drag, do nothing
        const pointer = event.dropPoint;
        const elements = this.tabRefs.toArray().map(el => el.nativeElement);
        const targetIndex = elements.findIndex((current) => {
            const currentRect = current.getBoundingClientRect();
            return pointer.x >= currentRect.left && pointer.x <= currentRect.right;
        });
 
        if (targetIndex === -1) {
            // if the pointer x is greater than the last item in the elements, then move the tab to the end
            if (pointer.x > elements[elements.length - 1].getBoundingClientRect().right) {
                this.setNewDasboardPosition(event.previousIndex, this.dashboardService.dashboards.length - 1);
                return;
            }
            // if the pointer x is less than the first item in the elements, then move the tab to the start
            if (pointer.x < elements[0].getBoundingClientRect().left) {
                this.setNewDasboardPosition(event.previousIndex, 0);
                return;
            }
        }
 
        if (targetIndex >= 0 && targetIndex != event.previousIndex && targetIndex < this.dashboardService.dashboards.length) {
            this.setNewDasboardPosition(event.previousIndex, targetIndex);
        }
    }
 
    setNewDasboardPosition(previousIndex: number, targetIndex: number): void {
        moveItemInArray(this.dashboardService.dashboards, previousIndex, targetIndex);
        const movedDashboard = this.dashboardService.dashboards[targetIndex];
        this.updateSelectedDashboard(movedDashboard);
    }
 
    updateSelectedDashboard(dashboard: Dashboard): void {
        this.editName = '';
        this.isEditingName = false;
        this.selectDashboard(dashboard);
    }
 
    /** Remove tab and open next available tab. If no tabs are left, create new tab */
    async closeDashboard(dashboard: Dashboard) {
        if (dashboard) {
            if (this.closingDashboard)
                return;

            // If publish is pending for the dashboard, get confirmation from the user
            if (dashboard.NeedConfirmationToClose) {
                const confirmText = this.localizationService.getCachedText(
                    'Dashboard_Tab',
                    'Close_Confirmation',
                    'Changes you made may not be saved.'
                );

                if (!window.confirm(confirmText)) {
                    return;
                }
            }

            this.closingDashboard = true;
            await this.dashboardService.removeDashboard(this.dashboardService.workspaceKey, dashboard.DashboardKey);
            this.closingDashboard = false;
        } else {
            // Close any new tabs
            for (let i = this.dashboardService.dashboards.length - 1; i >= 0; i--) {
                if (this.dashboardService.dashboards[i] == null) {
                    this.dashboardService.dashboards.splice(i, 1);
                    break;
                }
            }

            this.dashboardService.newTabSelectionView = WorkspaceNewTabViewMode.Landing;
            this.dashboardService.templateView = WorkspaceTemplateViewMode.Landing;
            if (this.dashboardService.dashboards.length > 0) {
                const index = this.dashboardService.dashboards.length - 1;
                this.selectDashboard(this.dashboardService.dashboards[index]);
            } else {
                this.dashboardService.activeDashboard.key = null;
                this.dashboardService.dashboards.push(null);
            }
        }
    }

    /** Create new tab */
    newTab(): void {
        const prevActiveDashboard = this.dashboardService.activeDashboard.key;
        this.dashboardService.activeDashboard.key = null;
        this.dashboardService.dashboards.push(null);
        this.dashboardService.triggerActiveDashboardChanged(null, prevActiveDashboard);
    }

    discardTemporaryDashboard(dashboardKey: string) {
        this.closeDashboard(this.dashboardService.dashboards.find(d => d.DashboardKey === dashboardKey));
        // When a template is closed, go back to the list of templates.
        // Open the new workspace window.
        const dashboards = this.dashboardService.dashboards;

        // Set the new tab to the template selection
        this.dashboardService.newTabSelectionView = WorkspaceNewTabViewMode.Templates;
        if (dashboards.length === 0 || dashboards[dashboards.length - 1] != null) {
            this.newTab();
        } else {
            this.selectDashboard(null);
        }
    }

    /**
     * Enable copy button if the user has create dashboard access
     */
    hasCreatePermission(): boolean {
        return !!this.dashboardService.permissions["DashboardNew"];
    }

    /** Open copy modal */
    async copy(dashboard: Dashboard) {
        if (!this.hasCreatePermission())
            return;

        const data: SaveDashboardModalData = {
            dashboardName: 'Copy of ' + (dashboard.DashboardName ? dashboard.DashboardName : ''),
            description: dashboard.DashboardDescription ? dashboard.DashboardDescription : '',
            folder: dashboard.DashboardGroup ? dashboard.DashboardGroup : '',
            key: dashboard.DashboardKey,
            workspaceKey: this.dashboardService.workspaceKey,
        };

        const prevActiveDashboard = this.dashboardService.activeDashboard.key;
        await this.dashboardService.openSaveDashboardModal(data, 'CopyDashboard').subscribe(async newDashboard => {
            if (this.dashboardService.dashboards.length >= 5) return; // Max tabs reached
            if (newDashboard) {
                for (let i = this.dashboardService.dashboards.length - 1; i >= 0; i--) {
                    if (this.dashboardService.dashboards[i] == null) {
                        this.dashboardService.dashboards.splice(i, 1);
                        break;
                    }
                }

                // open and select dashboard
                this.dashboardService.dashboards.push(newDashboard);
                this.dashboardService.activeDashboard.key = newDashboard.DashboardKey;
                this.dashboardService.triggerActiveDashboardChanged(newDashboard.DashboardKey, prevActiveDashboard);
                // Set new copied dashboard as active
                await this.dashboardService.setActiveDashboard(this.dashboardService.workspaceKey, this.dashboardService.activeDashboard.key, true);
            }
        });
    }

    canEdit(dashboard: Dashboard): boolean {
        if (this.dashboardService.availableDashboards.length === 0 || !dashboard) return false;
        const dash = this.dashboardService.availableDashboards.find(d => d.DashboardKey === dashboard.DashboardKey);
        return !dash?.SharedBy || dash?.SharedReadControlAccess;
    }

    canEditName(dashboard: Dashboard): boolean {
        if (this.dashboardService.availableDashboards.length === 0 || !dashboard) return false;
        const dash = this.dashboardService.availableDashboards.find(d => d.DashboardKey === dashboard.DashboardKey);
        return !dash?.SharedBy;
    }

    /** Open edit modal */
    async edit(dashboard: Dashboard) {
        const data: SaveDashboardModalData = {
            dashboardName: dashboard.DashboardName,
            description: dashboard.DashboardDescription,
            folder: dashboard.DashboardGroup,
            key: dashboard.DashboardKey,
        };

        const editedDash = await this.dashboardService.openSaveDashboardModal(data, 'EditDashboard').toPromise();
        if (editedDash) {
            const editedDashLocal = this.dashboardService.dashboards.find(d => d.DashboardKey === editedDash.DashboardKey);
            if (editedDashLocal) {
                editedDashLocal.DashboardName = editedDash.DashboardName;
                editedDashLocal.DashboardGroup = editedDash.DashboardGroup;
                editedDashLocal.DashboardDescription = editedDash.DashboardDescription;
            }
        }
    }

    /**
     * Enable timezone button if the user has write access to the selected dashboard and
     */
    canSetTimezone(dashboard: Dashboard): boolean {
        if (this.dashboardService.availableDashboards.length === 0 || dashboard === null) return false;
        const dash = this.dashboardService.availableDashboards.find(d => d.DashboardKey === dashboard.DashboardKey);
        const hasTimezones = this.timezoneService?.dataObj?.availableTimeZones?.length > 0;
        return hasTimezones && (!dash?.SharedBy || dash?.SharedReadControlAccess || dash?.SharedWriteAccess);
    }

    async setTimezone(dashboard: Dashboard, timezone: string = null) {
        if (!this.canSetTimezone(dashboard))
            return;

        // If not on dashboard, open dashboard
        if (dashboard.DashboardKey !== this.dashboardService.activeDashboard.key) {
            this.selectDashboard(dashboard);
        }

        dashboard.states.dashboardModules.loading = true;

        let requirePublish = false;
        try {
            requirePublish = await this.timezoneService.setDashboardTimezone(dashboard.DashboardKey, timezone);
            this.dashboardService.availableDashboards.find(
                (d) => d.DashboardKey === dashboard.DashboardKey
            ).TimeZoneInfoName = timezone;
            this.dashboardService.dashboards.find(
                (d) => d.DashboardKey === dashboard.DashboardKey
            ).TimeZoneInfoName = timezone;
        } finally {
            this.dashboardService.refreshTimeZoneInfo.next(dashboard.DashboardKey);

            if (requirePublish) {
                setTimeout(() => {
                    this.dashboardService.requestPublishNeeded.next(dashboard.DashboardKey);
                }, 3000);
            }
            dashboard.states.dashboardModules.loading = false;
        }
    }

    /**
     * Enable share button if the user has write access to the selected dashboard and
     */
    canShare(dashboard: Dashboard): boolean {
        if (this.dashboardService.availableDashboards.length === 0 || !dashboard) return false;
        const dash = this.dashboardService.availableDashboards.find(d => d.DashboardKey === dashboard.DashboardKey);
        return !dash?.SharedBy || dash?.SharedWriteAccess;
    }

    /** Get dashboards to assign and open assign modal */
    openAssignTool(dashboard: Dashboard): void {
        if (!this.canShare(dashboard))
            return;

        const dash = this.dashboardService.availableDashboards.find(d => d.DashboardKey === dashboard.DashboardKey);
        if (dash) {
            this.dashboardsToAssign = [dash];
            this.assignToolOpened = true;
        }
    }

    canShareURL(dashboard: Dashboard): boolean {
        return this.dashboardService.permissions["ShareViaURL"] && this.canShare(dashboard);
    }

    openShareURL(dashboard: Dashboard) {
        const dashboardToShare = this.dashboardService.availableDashboards.find(d => d.DashboardKey === dashboard.DashboardKey);
        const options = {
            dashboard: dashboardToShare,
        };
        this.shareViaURL.openModal(options, ShareViaUrlComponent);
    }

    /**
     * Enable subscribe button if the user has write access to the selected dashboard and
     */
    canSubscribe(dashboard: Dashboard): boolean {
        if (this.dashboardService.availableDashboards.length === 0 || !dashboard) return false;
        const dash = this.dashboardService.availableDashboards.find(d => d.DashboardKey === dashboard.DashboardKey);
        return !dash?.SharedBy || dash?.SharedWriteAccess;
    }

    /** Sets the required variables and opens scheduler */
    openScheduler(dashboard: Dashboard): void {
        if (!this.canSubscribe(dashboard))
            return;

        this.schedulerId = dashboard.DashboardID;
        this.schedulerKey = dashboard.DashboardKey;
        this.subscriptionTitle = dashboard.DashboardName;

        this.schedulerOpened = true;
    }

    /** Updates ag-grid and closes scheduler */
    public closeScheduler() {
        this.schedulerOpened = false;
    }

    /**
     * Enable export button if the user has write access to the selected dashboard and
     */
    canExport(dashboard: Dashboard): boolean {
        if (this.dashboardService.availableDashboards.length === 0 || !dashboard) return false;
        const dash = this.dashboardService.availableDashboards.find(d => d.DashboardKey === dashboard.DashboardKey);
        return !dash?.SharedBy || dash?.SharedReadControlAccess || dash?.SharedWriteAccess;
    }

    async export(dashboard: Dashboard, format: string | ModuleManagerExportFormat) {
        if (!this.canExport(dashboard))
            return;

        const formatDetails = this.dashboardService.exportFormats[format];
        const fileName = dashboard.DashboardName + " " + formatDetails.description + "_" + (new Date()).toString('yyyymmddhhmmss');

        const result = await this.dashboardService.getReport(dashboard.DashboardKey, format);
        this.moduleContainerService.downloadFile(result.data, formatDetails.mime, fileName + '.' + formatDetails.extension);
    }

    /**
     * Enable filter button if the user has write access to the selected dashboard
     */
    canFilter(dashboard: Dashboard): boolean {
        if (this.dashboardService.availableDashboards.length === 0 || !dashboard) return false;
        const dash = this.dashboardService.availableDashboards.find(d => d.DashboardKey === dashboard.DashboardKey);
        return !dash?.SharedBy || dash?.SharedReadControlAccess || dash?.SharedWriteAccess;
    }

    /** Open dashboard/master filter */
    openFilter(dashboard: Dashboard): void {
        if (!this.canFilter(dashboard))
            return;

        // If not on dashboard, open dashboard
        if (dashboard.DashboardKey !== this.dashboardService.activeDashboard.key) {
            this.selectDashboard(dashboard);
        }

        this.openSlideout = true;
        this.filterDashboardManager = this.dashboardService.dashboardManagers.get(dashboard.DashboardKey);
    }

    requestFullscreen(dashboard: Dashboard) {
        // If not on dashboard, open dashboard
        if (dashboard.DashboardKey !== this.dashboardService.activeDashboard.key){
            this.selectDashboard(dashboard);
        }

        // Toggle fullscreen on
        if (document.documentElement.requestFullscreen) {
            document.documentElement.requestFullscreen();
        } else if (document.documentElement['mozRequestFullScreen']) {
            document.documentElement['mozRequestFullScreen'](); // Firefox
        } else if (document.documentElement['webkitRequestFullscreen']) {
            document.documentElement['webkitRequestFullscreen'](); // Chrome and Safari
        } else if (document.documentElement['msRequestFullscreen']) {
            document.documentElement['msRequestFullscreen'](); // IE
        }
    }

    getDashboardName(index) {
        return this.dashboardService.dashboards[index].DashboardName;
    }

    /** Edit dashboard name from tab. */
    editDashboardName(dashboard: Dashboard, index) {
        //setup tab for editing
        this.isEditingName = true;
        this.focusDashboardNameInput(index);
        this.customName = this.getDashboardName(index);
        this.editName = this.getDashboardName(index);
    }

    enterPressed(dashboard, index, event) {
        if (event.keyCode === 13)
            this.checkEditNameChanges(dashboard, index, 'enter');
    }

    cancelEditDashboardName(dashboard: Dashboard, index) {
        this.editName = this.getDashboardName(index);
    }

    async checkEditNameChanges(dashboard: Dashboard, index, event) {
        const cancel = document.querySelector('#cancelName-' + index);
        const didClickCancel = cancel.contains(event.relatedTarget);
        const confirm = document.querySelector('#confirmName-' + index);
        const didClickConfirm = confirm?.contains(event.relatedTarget);

        // Only save on explicit actions: Enter key or confirm button click
        const shouldSave = (event === 'enter' || didClickConfirm) && this.editName !== '';

        try {
            if (didClickCancel || !shouldSave) {
                // Cancel: clicking outside, cancel button, or empty name - revert to original
                this.cancelEditDashboardName(dashboard, index);
                this.isEditingName = false;
            } else {
                // Save: update UI and persist to server
                const editedDash = this.dashboardService.dashboards[index];
                this.dashboardService.dashboards[index].DashboardName = this.editName;
                this.isEditingName = false;
                await this.dashboardService.updateDashboardDetails(
                    editedDash.DashboardKey,
                    this.editName,
                    editedDash.DashboardGroup,
                    editedDash.DashboardDescription,
                    editedDash.IsCXOne);
            }
        } finally {
            this.dashboardService.loadExistingDashboardsAndGroups();
        }
    }

    focusDashboardNameInput(index: number) {
        const inputEl = this.tabNameInputs?.find(e => e?.nativeElement?.id === `tabNameInput-${index}`)?.nativeElement;
        inputEl?.focus();
    }

    async updateDashboardAssignments(event) {
        const dash = await this.dashboardService.loadDashboardShareInfo(event[0].DashboardKey);
        const index = this.dashboardService.availableDashboards.findIndex(d => d.DashboardKey === dash.DashboardKey);
        this.dashboardService.availableDashboards[index] = dash;
    }

    updateDashboards() {
        this.dashboardService.loadExistingDashboardsAndGroups();
    }
}
